import { EmptyState, FlexColumn, Loading } from '@gorila-shared-ui/components';
import { Accordion, Panel } from 'baseui/accordion';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getHistoricDoorState } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeDoorState,
  loadingDoorState,
  reportDoorStates,
  reportEndDateState,
  reportSelectedAssetIdsState,
  reportStartDateState,
} from '../../../storage/reports';
import { HistoricDoorState } from '../../../types/currentState';
import { DoorStateReportTableFromAsset } from './DoorStateReportTableFromAsset';
import DoorStateReportTitle from './DoorStateReportTitle';

function DoorStateReports() {
  const { css } = useStyles();
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const setActiveReport = useSetRecoilState(activeDoorState);
  const [isloadingDoorState, setIsLoadingDoorState] = useRecoilState(loadingDoorState);
  const setReportHistorics = useSetRecoilState(reportDoorStates);
  const resetDoorHistorics = useResetRecoilState(reportDoorStates);

  useEffect(() => {
    return () => {
      resetDoorHistorics();
    };
  }, []);

  useEffect(() => {
    if (!selectedAssetIds?.length || !startDate || !endDate || !apiParams) return;

    const promises = selectedAssetIds.map((assetId) =>
      getHistoricDoorState(
        apiParams.clientId,
        assetId,
        startDate,
        endDate,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      ),
    );
    const loadHistoricDoor = async () => {
      setIsLoadingDoorState(true);
      const responses = await Promise.allSettled(promises);

      const newHistorics: { [key: string]: HistoricDoorState[] } = {};
      responses.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const {
            value: { historicDoor, error },
          } = result;
          if (!error) {
            newHistorics[selectedAssetIds[index]] = historicDoor ?? [];
          }
        }
      });
      setReportHistorics(newHistorics);

      if (newHistorics[selectedAssetIds[0]]) {
        setActiveReport(selectedAssetIds[0]);
      }
      setIsLoadingDoorState(false);
    };

    loadHistoricDoor();
  }, [selectedAssetIds, startDate, endDate]);

  if (isloadingDoorState) {
    return <Loading />;
  }

  if (!selectedAssetIds?.length) {
    return <EmptyState title="Selecciona un activo para visualizar el reporte." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flex: 1, overflow: 'hidden' })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, overflow: 'auto' }}>
            <Accordion
              key={selectedAssetIds[0]}
              initialState={{ expanded: [selectedAssetIds[0]] }}
              onChange={({ expanded }) => setActiveReport(expanded.length ? (expanded[0] as string) : undefined)}
            >
              {selectedAssetIds.map((assetId) => (
                <Panel
                  key={assetId}
                  title={<DoorStateReportTitle assetId={assetId} />}
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  <DoorStateReportTableFromAsset assetId={assetId} />
                </Panel>
              ))}
            </Accordion>
          </div>
        )}
      </AutoSizer>
    </FlexColumn>
  );
}

export default DoorStateReports;
